
import {
  defineComponent,
  h,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useReCaptcha } from "vue-recaptcha-v3";
import { injectScrollToTop } from "@/hooks/useScrollbar";
import { dateFormat } from "@/utils/date";
import {
  CheckMsg,
  ComfirmMsg,
  ConfirmText,
  ErrorMsg,
  InfoMsg,
} from "@/constants/message.constant";
import { injectReloadFunc } from "@/hooks/useReload";
import { getToiawaseJokyoList } from "@/api/masterTable.api";
import { toiawaseJokyoMaster } from "@/interfaces/masterTable.interface";
import {
  createToiawase,
  getToiawase,
  sendToiawaseKaito,
  updateToiawase,
} from "@/api/toiawase.api";
import { ElMessage } from "element-plus";

// reCAPTCHAのトークン取得タイムアウト(ミリ秒)
const RECAPTCHA_TIMEOUT_MS = 10000;

// 指定時間内に解決しない場合は reject する
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

export default defineComponent({
  setup() {
    injectScrollToTop();
    const recaptchaInstance = useReCaptcha();

    // この画面を表示している間だけ reCAPTCHA バッジを表示する
    // (main.ts で autoHideBadge:true のため、既定では全画面で非表示)
    onMounted(async () => {
      try {
        await recaptchaInstance?.recaptchaLoaded();
        recaptchaInstance?.instance.value?.showBadge();
      } catch (e) {
        // reCAPTCHAの読み込みに失敗した場合はバッジを表示しない
        // (送信時に改めてエラーメッセージを表示するため、ここでは何もしない)
      }
    });
    onUnmounted(() => {
      recaptchaInstance?.instance.value?.hideBadge();
    });
    const panels = reactive({
      toiawaseForm: {
        id: "",
        toiawaseId: "",
        createdDate: "",
        kjnNm: "",
        kaishaNm: "",
        email: "",
        toiawaseKenmei: "",
        toiawaseNaiyo: "",
      },
      kaitoForm: {
        toiawaseJokyoCd: "",
        kaitoNaiyo: "",
        tantoNm: "",
        updatedDate: "",
      },
    });
    const toiawaseFormRef = ref();
    const loadings = reactive({
      createBtn: false,
      updateBtn: false,
      sendBtn: false,
    });
    const isAnswered = reactive({
      answered: false,
    });
    const toiawaseJokyoOptions = ref([] as toiawaseJokyoMaster[]); //対応状況選択リスト
    const router = useRouter();
    const route = useRoute();
    const reload = injectReloadFunc();
    const handleReload = () => {
      if (panels.toiawaseForm.id) {
        reload();
      } else {
        router.push({ name: "shinseiList" });
      }
    };
    const confirmDialogVisible = ref(false);

    // 入力情報のチェックルール;
    const toiawaseFormRules = {
      kjnNm: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_KJN_NM2,
        },
      ],
      kaishaNm: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_KAISHA_NM,
        },
      ],
      email: [
        {
          type: "email",
          trigger: "blur",
          message: CheckMsg.CHK_MAIL2,
        },
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_MAIL,
        },
      ],
      toiawaseKenmei: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_TOIAWASE_KENMEI,
        },
      ],
      toiawaseNaiyo: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_TOIAWASE_NAIYO,
        },
      ],
    };

    // 画面情報セット
    // 問い合わせのシーケンスidを取得
    const id = route.params.id;
    const isExitedToiawase = Boolean(id);
    // 情報セット
    const setDetailInfo = async () => {
      if (isExitedToiawase) {
        const {
          data: { result },
        } = await getToiawase(id as string);
        panels.toiawaseForm.id = result.id;
        panels.toiawaseForm.toiawaseId = result.toiawase_id;
        panels.toiawaseForm.createdDate = dateFormat(result.toroku_date);
        panels.toiawaseForm.kjnNm = result.kjn_nm;
        panels.toiawaseForm.kaishaNm = result.kaisha_nm;
        panels.toiawaseForm.email = result.mail_address;
        panels.toiawaseForm.toiawaseKenmei = result.toiawase_kenmei;
        panels.toiawaseForm.toiawaseNaiyo = result.toiawase_naiyo;

        panels.kaitoForm.toiawaseJokyoCd = result.jokyo.toiawase_jokyo_cd;
        panels.kaitoForm.kaitoNaiyo = result.kaito_naiyo;
        panels.kaitoForm.tantoNm = result.tanto_shoninsha?.tanto_nm;
        panels.kaitoForm.updatedDate =
          result.koshin_date && dateFormat(result.koshin_date);
        isAnswered.answered = Boolean(panels.kaitoForm.kaitoNaiyo);

        //対応状況選択項目
        toiawaseJokyoOptions.value = await getToiawaseJokyoList();
      }
    };
    setDetailInfo();

    // 各種ボタン押下イベント
    const handleCreate = () => {
      toiawaseFormRef.value?.validate(async (valid: unknown) => {
        if (valid) {
          loadings.createBtn = true;

          // reCAPTCHA v3 トークン取得
          // 応答が返らない場合に送信ボタンが固まらないようタイムアウトを設ける
          let recaptchaToken = "";
          try {
            recaptchaToken = await withTimeout(
              (async () => {
                await recaptchaInstance?.recaptchaLoaded();
                return (
                  (await recaptchaInstance?.executeRecaptcha(
                    "toiawase_create"
                  )) || ""
                );
              })(),
              RECAPTCHA_TIMEOUT_MS
            );
          } catch (e) {
            loadings.createBtn = false;
            ElMessage({
              type: "error",
              message: ErrorMsg.ERR_RECAPTCHA_LOAD,
              duration: 6000,
              showClose: true,
            });
            return;
          }

          // ライブラリがundefinedを返した場合など、例外にならずトークンが空になるケース
          // (空トークンのままPOSTするとサーバ側の一般エラーになり原因が分かりにくいため、
          //  ここで明示的にreCAPTCHAのエラーとして扱う)
          if (!recaptchaToken) {
            loadings.createBtn = false;
            ElMessage({
              type: "error",
              message: ErrorMsg.ERR_RECAPTCHA_LOAD,
              duration: 6000,
              showClose: true,
            });
            return;
          }

          //登録APIの失敗のみをcatchする(成功後の処理はcatch対象に含めない)
          try {
            await createToiawase({
              kjnNm: panels.toiawaseForm.kjnNm,
              kaishaNm: panels.toiawaseForm.kaishaNm,
              email: panels.toiawaseForm.email,
              toiawaseKenmei: panels.toiawaseForm.toiawaseKenmei,
              toiawaseNaiyo: panels.toiawaseForm.toiawaseNaiyo,
              recaptchaToken,
            });
          } catch (error) {
            //エラーメッセージは request.ts の interceptor が表示するため、ここでは表示しない
            loadings.createBtn = false;
            return;
          }

          ElMessage({
            type: "success",
            message: h(
              "p",
              {
                style: `color: #0f885e;
                          font-size:13px;
                          line-height:20px`,
              },
              [
                h("p", null, InfoMsg.INFO_TOIAWASE_COMPLETED_PART1),
                h("p", null, InfoMsg.INFO_TOIAWASE_COMPLETED_PART2),
              ]
            ),
            duration: 6000,
            showClose: true,
          });
          loadings.createBtn = false;
          handleReload();
        } else {
          return false;
        }
      });
    };
    const handleUpdate = async () => {
      loadings.updateBtn = true;
      try {
        await updateToiawase(id as string, {
          toiawaseJokyoCd: panels.kaitoForm.toiawaseJokyoCd,
          kaitoNaiyo: panels.kaitoForm.kaitoNaiyo,
        });
        ElMessage({
          type: "success",
          message: InfoMsg.INFO_TOIAWASE_UPDATED,
        });
        loadings.updateBtn = false;
        handleReload();
      } catch (error) {
        loadings.updateBtn = false;
      }
    };
    //キャンセルボタン押下
    const handleCancel = () => {
      router.back();
    };
    //回答内容送信
    const handleSendKaito = async () => {
      loadings.sendBtn = true;
      isAnswered.answered = true;
      try {
        await sendToiawaseKaito(id as string, {
          email: panels.toiawaseForm.email,
          kjnNm: panels.toiawaseForm.kjnNm,
          kaishaNm: panels.toiawaseForm.kaishaNm,
          toiawaseNaiyo: panels.toiawaseForm.toiawaseNaiyo,
          kaitoNaiyo: panels.kaitoForm.kaitoNaiyo,
        });
        ElMessage({
          type: "success",
          message: InfoMsg.INFO_TOIAWASE_KAITO_SENT,
        });
      } finally {
        loadings.sendBtn = false;
        confirmDialogVisible.value = false;
      }
    };

    return {
      panels,
      toiawaseFormRef,
      toiawaseFormRules,
      isExitedToiawase,
      toiawaseJokyoOptions,
      loadings,
      isAnswered,
      handleCreate,
      handleUpdate,
      handleCancel,
      handleSendKaito,
      ConfirmText,
      confirmDialogVisible,
      ComfirmMsg,
    };
  },
});
